<template>
  <div>
    <!-- Dialog de Detalhes (lista de alergias) -->
    <v-dialog
      v-if="!openDirectAdd"
      v-model="internalValue"
      transition="dialog-bottom-transition"
      max-width="1000px"
      scrollable
    >
      <v-card class="ds-dialog">
        <v-card-title
          class="ds-dialog-header d-flex justify-space-between align-center"
        >
          <div class="d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
            <span class="ds-dialog-title">{{ dialogTitle }}</span>
          </div>
          <div class="d-flex align-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="white"
                  @click="openAddDialog"
                  class="mr-1"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
              <span>Adicionar alergia</span>
            </v-tooltip>
            <v-btn
              icon
              color="white"
              class="close-btn"
              @click="closeDetailsDialog"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <section v-if="allergies.length" class="ds-form-section ds-form-section--flush">
            <v-simple-table class="history-table">
              <thead>
                <tr>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4">
                    Alérgeno
                  </th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4">
                    Reação
                  </th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4" style="width: 140px">
                    Severidade
                  </th>
                  <th class="text-left text-uppercase caption font-weight-bold pa-4" style="width: 90px">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in allergies" :key="item.id" class="history-row">
                  <td class="pa-4">
                    <span class="body-2 font-weight-medium">{{ item.allergen }}</span>
                  </td>
                  <td class="pa-4">
                    <span class="body-2">{{ item.reaction || "—" }}</span>
                  </td>
                  <td class="pa-4">
                    <v-chip
                      v-if="item.severity"
                      x-small
                      :color="severityColor(item.severity)"
                      text-color="white"
                    >
                      {{ severityLabel(item.severity) }}
                    </v-chip>
                    <span v-else class="body-2">—</span>
                  </td>
                  <td class="pa-4">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          color="catRed"
                          class="action-btn mr-1"
                          @click="openLinker(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small>mdi-link-variant</v-icon>
                        </v-btn>
                      </template>
                      <span>Vincular</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          color="error"
                          class="action-btn"
                          @click="confirmDelete(item)"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small>mdi-trash-can-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>Excluir</span>
                    </v-tooltip>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </section>

          <!-- Estado vazio -->
          <section v-else class="ds-form-section text-center py-8 px-4">
            <v-icon size="56" color="grey lighten-1">mdi-alert-circle-outline</v-icon>
            <p class="body-1 grey--text mt-3 mb-0">Nenhuma alergia registrada.</p>
            <v-btn color="catRed" outlined small class="mt-4" @click="openAddDialog">
              <v-icon small left>mdi-plus</v-icon>
              Adicionar primeira alergia
            </v-btn>
          </section>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog de Adicionar -->
    <v-dialog v-model="formDialogVisible" max-width="600px" scrollable>
      <v-card class="ds-dialog">
        <v-card-title
          class="ds-dialog-header d-flex justify-space-between align-center"
        >
          <div class="d-flex align-center" style="position: relative; z-index: 1">
            <v-icon color="white" class="mr-2">mdi-plus-circle-outline</v-icon>
            <span class="ds-dialog-title">Adicionar alergia</span>
          </div>
          <v-btn icon color="white" class="close-btn" @click="closeFormDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-allergy</v-icon>
                <span>Alergias</span>
              </div>
            </div>
            <CatalogCheckboxPicker
              v-model="selectedItems"
              :catalog="catalog"
              :loading="loadingCatalog"
              color="catRed"
            />
          </section>
        </v-card-text>

        <v-card-actions class="ds-dialog-actions">
          <v-btn outlined color="primary" @click="closeFormDialog">
            <v-icon left>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="catRed"
            dark
            :disabled="!canSave"
            :loading="formLoading"
            depressed
            class="px-6"
            @click="saveData"
          >
            <v-icon small left>mdi-plus</v-icon>
            Adicionar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Vincular alergia ↔ doença (correlação) -->
    <CorrelationLinker
      v-model="linkerOpen"
      source-type="allergy"
      :source-id="linkerAllergy ? linkerAllergy.id : null"
      :source-label="linkerAllergy ? linkerAllergy.allergen : ''"
      :patient-id="patientId"
      @changed="$emit('data-saved')"
    />
  </div>
</template>

<script>
import { useSnackbar } from "@/composables/useSnackbar";
import {
  createAllergies,
  deleteAllergy,
  getAllergyCatalog,
} from "@/services/allergyService";
import { getConditionsByPatient } from "@/services/conditionService";
import CorrelationLinker from "@/features/patients/profile-dashboard/shared/CorrelationLinker.vue";
import CatalogCheckboxPicker from "@/features/patients/profile-dashboard/fields/CatalogCheckboxPicker.vue";

export default {
  name: "EditAllergiesDialog",

  components: {
    CorrelationLinker,
    CatalogCheckboxPicker,
  },

  props: {
    value: { type: Boolean, default: false },
    dialogTitle: { type: String, default: "Alergias do paciente" },
    allergies: { type: Array, default: () => [] },
    patientId: { type: [String, Number], required: true },
    openDirectAdd: { type: Boolean, default: false },
  },

  setup() {
    const { success, error, warning, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, erroDeRequisicao };
  },

  data() {
    return {
      formDialogVisible: false,
      selectedItems: [],
      formLoading: false,
      deleteLoading: false,
      itemToDelete: null,
      catalog: [],
      loadingCatalog: false,
      // Correlação alergia ↔ doença
      linkerOpen: false,
      linkerAllergy: null,
      conditions: [],
      loadingConditions: false,
    };
  },

  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    canSave() {
      return !this.formLoading && this.selectedItems.length > 0;
    },
  },

  watch: {
    value(val) {
      if (val && this.openDirectAdd) {
        this.$nextTick(() => this.openAddDialog());
      }
    },
    formDialogVisible(val) {
      if (!val && this.openDirectAdd) {
        this.$emit("input", false);
      }
    },
  },

  methods: {
    severityLabel(severity) {
      return (
        { mild: "Leve", moderate: "Moderada", severe: "Grave" }[severity] || ""
      );
    },

    severityColor(severity) {
      return (
        { mild: "amber darken-2", moderate: "orange darken-2", severe: "red darken-2" }[
          severity
        ] || "grey"
      );
    },

    closeDetailsDialog() {
      this.$emit("input", false);
    },

    async openAddDialog() {
      this.selectedItems = [];
      this.formDialogVisible = true;
      if (this.catalog.length === 0) {
        this.loadingCatalog = true;
        try {
          this.catalog = await getAllergyCatalog();
        } catch (err) {
          console.error("Erro ao carregar catálogo de alergias:", err);
          this.catalog = [];
        } finally {
          this.loadingCatalog = false;
        }
      }
    },

    closeFormDialog() {
      this.formDialogVisible = false;
      this.selectedItems = [];
    },

    async saveData() {
      if (this.selectedItems.length === 0) {
        this.warning("Selecione ao menos uma alergia");
        return;
      }
      this.formLoading = true;
      try {
        await createAllergies(
          this.patientId,
          this.selectedItems.map((s) => ({
            allergen: s.name,
            allergy_catalog_id: s.catalog_id || null,
          })),
        );
        // salvia-781: guarda antes de fechar o form, que limpa a selecao.
        const registrado = this.selectedItems;
        this.success("Alergia(s) adicionada(s) com sucesso!");
        this.closeFormDialog();
        this.$emit("data-saved", registrado);
      } catch (err) {
        console.error("Erro ao salvar alergia:", err);
        this.erroDeRequisicao(err, err.response?.data?.message || "Erro ao salvar a alergia");
      } finally {
        this.formLoading = false;
      }
    },

    async openLinker(allergy) {
      this.linkerAllergy = allergy;
      if (this.conditions.length === 0) {
        this.loadingConditions = true;
        try {
          this.conditions = await getConditionsByPatient(this.patientId);
        } catch (err) {
          console.error("Erro ao carregar condições:", err);
          this.conditions = [];
        } finally {
          this.loadingConditions = false;
        }
      }
      this.linkerOpen = true;
    },

    async confirmDelete(item) {
      this.itemToDelete = item;
      const ok = await this.$confirm({
        type: "delete",
        title: "Confirmar exclusão",
        message: `Tem certeza que deseja remover "${item.allergen}"?`,
        hint: "Esta ação não poderá ser desfeita.",
      });
      if (!ok) {
        this.itemToDelete = null;
        return;
      }
      await this.deleteData();
    },

    async deleteData() {
      if (!this.itemToDelete) return;
      this.deleteLoading = true;
      try {
        await deleteAllergy(this.itemToDelete.id);
        this.success("Alergia removida com sucesso!");
        this.itemToDelete = null;
        this.$emit("data-deleted");
      } catch (err) {
        console.error("Erro ao excluir alergia:", err);
        this.erroDeRequisicao(err, err.response?.data?.message || "Erro ao remover a alergia");
      } finally {
        this.deleteLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ═══ Design system dialog (tema Vermelho — Alergias) ═══ */
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}

.ds-dialog-header {
  background: linear-gradient(135deg, var(--ds-primary) 0%, var(--ds-primary-deep) 100%);
  color: var(--ds-white);
}

.ds-dialog-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.ds-dialog-header :deep(.v-btn) {
  position: relative;
  z-index: 1;
}

.ds-dialog-header :deep(.v-btn:hover) {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

.history-table thead th {
  background-color: var(--ds-primary-surface) !important;
  border-bottom: 2px solid var(--ds-primary-border) !important;
  letter-spacing: 0.4px;
  color: var(--ds-primary-deep) !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
}

.history-row {
  transition: background-color 0.15s ease;
}

.history-row:hover {
  background-color: var(--ds-primary-surface) !important;
}

.history-row td {
  border-bottom: 1px solid var(--ds-primary-border) !important;
  color: var(--ds-text-600);
}

.action-btn {
  transition: transform 0.15s ease, background-color 0.15s ease;
}

.action-btn:hover {
  transform: scale(1.15);
}
</style>
